import React from "react";
import { Button } from "@pd/pd-ui-kit";
import { useTranslation } from "react-i18next";

import Modal from "~/shared/ui/Fields/Modal";
import { useGetPartnerById } from "~/entity/Partners";
import {
  useURLParamsGetter,
  useURLParamsSetter,
} from "~/hooks/useURLParamsSetter";
import { getFormatDate } from "~/libs/getFormatDate";

import s from "./PartnersViewModal.less";

const VIEW_FIELDS = [
  "id",
  "name",
  "inn",
  "status",
  "country",
  "fo",
  "region",
  "city",
  "address",
] as const;

const PartnersViewModal = () => {
  const { t } = useTranslation();
  const { openModal, currentId } = useURLParamsGetter<{
    openModal: string;
    currentId: string;
  }>();
  const setParams = useURLParamsSetter();
  const isOpen = openModal === "View";
  const { partner, isLoading } = useGetPartnerById(currentId, isOpen);

  const closeModal = () =>
    setParams({ openModal: undefined, currentId: undefined });

  const getFieldValue = (field: (typeof VIEW_FIELDS)[number]) => {
    if (!partner) {
      return "";
    }

    const value = partner[field];
    return value || "—";
  };

  const formatMetaDate = (value?: string) =>
    value ? getFormatDate(value) : "—";

  return (
    <Modal
      isOpen={isOpen}
      actionClose={closeModal}
      w={600}
      withCloseBtn={false}
      withoutMargin
      customClassName={s.modal}
    >
      <div className={s.content}>
        <p className={s.title}>{t("partners.viewModal.title")}</p>

        <div className={s.body}>
          <div className={s.section}>
            <p className={s.sectionTitle}>{t("partners.modal.section")}</p>
            <div className={s.rows}>
              {VIEW_FIELDS.map((field, index) => (
                <div
                  key={field}
                  className={s.row}
                  data-striped={index % 2 === 1}
                  data-address={field === "address"}
                >
                  <span className={s.label}>
                    {t(
                      field === "address"
                        ? "partners.table.address"
                        : `partners.filter.${field}`,
                    )}
                  </span>
                  <span className={s.value}>
                    {isLoading ? "..." : getFieldValue(field)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className={s.meta}>
            <div className={s.divider} />
            <div className={s.metaItems}>
              <div className={s.metaItem}>
                <span className={s.metaLabel}>
                  {t("partners.viewModal.author")}
                </span>
                <span className={s.metaValue}>
                  {isLoading ? "..." : partner?.author || "—"}
                </span>
              </div>
              <div className={s.metaItem}>
                <span className={s.metaLabel}>
                  {t("partners.viewModal.createdAt")}
                </span>
                <span className={s.metaValue}>
                  {isLoading ? "..." : formatMetaDate(partner?.createdAt)}
                </span>
              </div>
              <div className={s.metaItem}>
                <span className={s.metaLabel}>
                  {t("partners.viewModal.updatedAt")}
                </span>
                <span className={s.metaValue}>
                  {isLoading ? "..." : formatMetaDate(partner?.updatedAt)}
                </span>
              </div>
            </div>
            <div className={s.divider} />
          </div>
        </div>

        <div className={s.buttons}>
          <Button
            bold
            color="secondary"
            fontSize="14"
            size="medium"
            radius={5}
            onClick={closeModal}
          >
            {t("partners.viewModal.close")}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default PartnersViewModal;
